import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Calendar, Cake, Skull, ArrowRight, RotateCcw, X } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { EventCard } from "@/components/EventCard";
import { HoroscopePanel } from "@/components/HoroscopePanel";
import { TeaserCards, WhyStrip } from "@/components/ValueSection";
import { useAuth } from "@/context/AuthContext";
import { BirthdayCountdown } from "@/components/BirthdayCountdown";
import { MobileBirthdayBar } from "@/components/MobileBirthdayBar";
import { ObservanceSection } from "@/components/ObservanceSection";
import { NumberOnesSection } from "@/components/NumberOnesSection";
import { SportsSection } from "@/components/Sports";
import { OnThisDayReel } from "@/components/OnThisDayReel";
import { HallOfFameReel } from "@/components/HallOfFame";
import { HeadlineTicker } from "@/components/HeadlineTicker";
import { DiedOnThisDay } from "@/components/TodayHighlights";
import { TodayInHistoryList } from "@/components/TodayInHistoryList";
import { SectionJumps } from "@/components/SectionJumps";
import { BattlesSection } from "@/components/Battles";
import { MoviesSection } from "@/components/Movies";
import { cleanEvents, personName, MONTHS, setSeo } from "@/lib/events";

const AD_IN1 = { id: "adsense-in1", slot: "IN1", size: "rect336" };

const PeopleSection = ({ id, label, icon: Icon, color, empty, events, intro, showMoreLabel }) => {
  const [expanded, setExpanded] = useState(false);
  const INITIAL = 6;
  const shown = expanded ? events : events.slice(0, INITIAL);
  return (
    <section id={id} data-testid={`${id}-section`} className="mt-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${color}`} />
          <h2 className="text-2xl font-bold text-ink">{label}</h2>
          <span data-testid={`${id}-count`} className="ml-1 text-[12px] font-semibold text-ink-muted">{events.length}</span>
        </div>
        {intro && <p className="text-[13px] text-ink-muted">{intro}</p>}
      </div>
      {events.length === 0 ? (
        <p className="text-sm text-ink-muted">{empty}</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {shown.map((e, i) => (
              <div key={e.id} className="relative">
                {e.death_cause && (
                  <span data-testid={`death-cause-${e.id}`} className="absolute -top-2 left-4 z-10 px-2.5 py-0.5 rounded-full bg-ink text-gold text-[10px] font-bold uppercase tracking-wider shadow-sm">{e.death_cause}</span>
                )}
                <EventCard e={e} i={i} total={shown.length} />
              </div>
            ))}
          </div>
          {events.length > INITIAL && (
            <button type="button" data-testid={`${id}-toggle`} onClick={() => setExpanded(!expanded)} className="mt-4 inline-flex items-center px-5 h-10 rounded-full border border-ink text-ink text-sm font-semibold hover:bg-ink hover:text-white transition-colors">
              {expanded ? "Show fewer" : `${showMoreLabel} (${events.length - INITIAL} more)`}
            </button>
          )}
        </>
      )}
    </section>
  );
};

const Coincidences = ({ coinc, hl, dateLabel }) => (
  <div data-testid="coincidences-view">
    <PeopleSection id="births" label="Born on this day" icon={Cake} color="text-gold" empty="No recorded births." events={coinc?.births || []} showMoreLabel="Show all births"
      intro={<Link to={`/birthdays?date=${coinc?.date_dd_mm || ""}`} data-testid="births-all-link" className="font-semibold text-gold hover:underline">All birthdays {dateLabel === "today" ? "today" : `on ${dateLabel}`} + find your twins →</Link>} />
    <AdSlot id="adsense-mobile-square" slot="MOBILE_SQ" size="rect300" className="md:hidden mt-8" />
    <DiedOnThisDay hl={hl} dateLabel={dateLabel} />
  </div>
);

export default function OnThisDay() {
  const now = new Date();
  const [params, setParams] = useSearchParams();
  const initial = /^\d{2}-\d{2}$/.test(params.get("date") || "") ? params.get("date").split("-").map(Number) : null;
  const initialYear = /^\d{1,4}$/.test(params.get("year") || "") ? params.get("year") : "";
  const [day, setDay] = useState(initial ? initial[0] : now.getDate());
  const [month, setMonth] = useState(initial ? initial[1] : now.getMonth() + 1);
  const [year, setYear] = useState(initialYear);
  const [appliedYear, setAppliedYear] = useState(initialYear);
  const [data, setData] = useState(null);
  const [coinc, setCoinc] = useState(null);
  const [hl, setHl] = useState(null);
  const [horoscope, setHoroscope] = useState(null);
  const [activeSign, setActiveSign] = useState("aries");
  const [loading, setLoading] = useState(true);
  const [explored, setExplored] = useState(!!initial);
  const { user } = useAuth();

  const ddmm = `${String(day).padStart(2, "0")}-${String(month).padStart(2, "0")}`;
  const todayKey = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const isToday = ddmm === todayKey && !appliedYear;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [allRes, coRes, short] = await Promise.all([
        api.get(`/events/${ddmm}/all`),
        api.get(`/coincidences/${ddmm}`),
        api.get(`/events/${ddmm}`),
      ]);
      api.get(`/events/${ddmm}/highlights`).then((r) => setHl(r.data)).catch(() => setHl(null));
      setData({ ...allRes.data, events: cleanEvents(allRes.data.events), count: cleanEvents(allRes.data.events).length, sports: cleanEvents(allRes.data.sports) });
      const births = cleanEvents(coRes.data.births), deaths = cleanEvents(coRes.data.deaths);
      setCoinc({ ...coRes.data, births, deaths });
      const people = births.slice(0, 20);
      if (people.length) {
        api.get("/portraits", { params: { names: people.map((e) => personName(e.event_title)).join("|") } }).then((pr) => {
          const withFace = (e) => ({ ...e, portrait: pr.data.portraits[personName(e.event_title)]?.image || null });
          setCoinc((c) => (c ? { ...c, births: c.births.map(withFace) } : c));
        }).catch(() => {});
      }
      setHoroscope(short.data.horoscope);
    } finally {
      setLoading(false);
    }
  }, [ddmm]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const lbl = `${day} ${MONTHS[month - 1]}`;
    setSeo({
      title: isToday ? `Today in History – ${lbl}: births, deaths, battles & No.1 hits | OddlyTrue` : `${lbl} in History – who was born, who died and what happened | OddlyTrue`,
      description: `What happened on ${lbl}? Every verified event, famous birthday, death, battle and chart-topping No.1 for ${lbl}, with an original editorial introduction and today's horoscope for all 12 signs.`,
      canonical: isToday ? "https://oddlytrue.co/" : `https://oddlytrue.co/?date=${ddmm}`,
    });
  }, [ddmm, isToday, day, month]);
  useEffect(() => {
    if (!loading && window.location.hash) document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loading]);

  const explore = () => {
    setAppliedYear(year); setExplored(true);
    try { localStorage.setItem("oddlytrue_birthday", ddmm); } catch (e) { /* ignore */ }
    load();
    setTimeout(() => document.querySelector("[data-testid=events-heading]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const resetToToday = useCallback(() => {
    const t = new Date();
    setDay(t.getDate()); setMonth(t.getMonth() + 1); setYear(""); setAppliedYear(""); setExplored(false);
    if (params.get("date") || params.get("year")) setParams({}, { replace: true });
  }, [params, setParams]);

  // Home must show *today*: when the tab is re-opened on a later day (mobile browsers keep SPA state for days) or
  // after a long time hidden, snap back to the current date unless a ?date= link was explicitly followed.
  useEffect(() => {
    let hiddenAt = null;
    const key = (d) => `${d.getDate()}-${d.getMonth() + 1}`;
    let loadedDay = key(new Date());
    const onVisibility = () => {
      if (document.visibilityState === "hidden") { hiddenAt = Date.now(); return; }
      const dayChanged = key(new Date()) !== loadedDay;
      const longAway = hiddenAt && Date.now() - hiddenAt > 30 * 60 * 1000;
      if ((dayChanged || longAway) && !new URLSearchParams(window.location.search).get("date")) {
        loadedDay = key(new Date());
        resetToToday();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", (e) => { if (e.persisted) onVisibility(); });
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [resetToToday]);
  const savedBirthday = (user?.dob || "").slice(5).split("-").reverse().join("-") || (() => { try { return localStorage.getItem("oddlytrue_birthday") || ""; } catch (e) { return ""; } })();
  const isMyBirthday = savedBirthday === ddmm;

  const events = (data?.events || []).filter((e) => !appliedYear || String(e.year) === String(appliedYear));
  const daysInMonth = new Date(2024, month, 0).getDate();
  const dateLabel = `${day} ${MONTHS[month - 1]}`;
  const lineCount = events.length + (data?.sports || []).length + (coinc?.births || []).length + (hl?.deaths || []).length + (hl?.battles || []).length + (hl?.number_ones || []).length;
  const countText = appliedYear ? `showing ${appliedYear} only` : `${lineCount} ${lineCount === 1 ? "entry" : "entries"}`;

  const selectClass = "px-3 py-2.5 rounded-xl bg-white border border-[#e5e7eb] text-ink focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/40 text-sm font-medium";

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const offset = window.innerWidth < 768 ? 136 : 96;  // sticky header (+ mobile jump strip)
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior: "smooth" });
  };

  const datePicker = (
    <div className="p-5 rounded-2xl border border-[#e5e7eb] bg-white">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-4 h-4 text-gold" />
        <span className="text-xs uppercase tracking-widest text-ink-muted">Pick another date or enter your birthday (day + month only)</span>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-muted">Day</span>
          <select data-testid="calendar-day" value={day} onChange={(e) => setDay(Number(e.target.value))} className={selectClass}>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-muted">Month</span>
          <select data-testid="calendar-month" value={month} onChange={(e) => setMonth(Number(e.target.value))} className={selectClass}>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-muted">Year (optional)</span>
          <span className="relative">
            <input data-testid="calendar-year" value={year} onChange={(e) => setYear(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))} placeholder="Filter by year - e.g. 1969" className={selectClass + " w-52 pr-9"} />
            {year && (
              <button type="button" data-testid="calendar-year-clear" aria-label="Clear year" onClick={() => { setYear(""); if (appliedYear) setAppliedYear(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-ink-muted hover:bg-paper hover:text-ink flex items-center justify-center">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </span>
        </label>
        <button data-testid="calendar-search" onClick={explore} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-ink text-white font-semibold text-sm hover:bg-gold hover:text-ink transition-colors">
          Explore <ArrowRight className="w-4 h-4" />
        </button>
        <button type="button" data-testid="calendar-reset" onClick={() => { resetToToday(); setTimeout(() => document.querySelector("[data-testid=events-heading]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border font-semibold text-sm transition-colors ${isToday ? "border-[#e5e7eb] bg-white text-ink-muted hover:border-gold hover:text-ink" : "border-gold bg-paper text-ink hover:bg-gold"}`}>
          <RotateCcw className="w-4 h-4" /> {isToday ? "Showing today" : "Back to today"}
        </button>
      </div>
      {!isToday && (
        <p data-testid="calendar-status" className="mt-3 text-xs text-ink-muted">Viewing <strong className="text-ink">{dateLabel}{appliedYear ? ` ${appliedYear}` : ""}</strong> — today is {now.getDate()} {MONTHS[now.getMonth()]}.</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-paper-light">
      <Header />

      <SectionJumps onJump={scrollTo} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6">
        <AdSlot id="adsense-top" slot="TOP" size="leaderboard" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        <section id="hero">
          <p data-testid="date-label" className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink-muted mb-3">
            {now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
          <h1 data-testid="home-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ink">On This Day</h1>
          <p className="mt-3 text-base text-ink-muted max-w-2xl">What happened, who was born, who we lost — plus today's horoscope for every sign.</p>
          <div data-testid="coverage-badge" className="bg-paper border-l-4 border-gold rounded-r-xl px-5 py-3 mt-4 text-[14px] leading-[1.5] text-ink max-w-2xl">
            <span className="font-semibold">Discover thousands of historical events</span> that happened on this day, your birthday, or any other day of the year. <span className="text-ink-muted">1,000s of searchable events.</span>
          </div>
          <BirthdayCountdown />
        </section>

        <TeaserCards events={[...(data?.events || []), ...(data?.sports || []), ...(coinc?.births || []), ...(coinc?.deaths || [])]} ddmm={data?.date_dd_mm || ddmm} onToday={() => scrollTo("today-in-history")} />
        <HeadlineTicker ddmm={data?.date_dd_mm || ddmm} />

        {/* Mobile: events → horoscope stacked. Desktop: events left, horoscope sticky right. */}
        <div className="flex flex-col gap-8 lg:grid lg:grid-cols-[1fr_32%] lg:items-start">
          <section id="today-in-history" className="scroll-mt-24">
            {!loading && (explored || isMyBirthday) && (coinc?.births || []).length > 0 && (
              <div data-testid="birthday-twins" className="mb-8 bg-paper border border-gold rounded-2xl p-5">
                <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold mb-1">Birthday twins</p>
                <h2 data-testid="birthday-twins-heading" className="text-xl font-bold text-ink">
                  {isMyBirthday ? `You share your ${dateLabel} birthday with...` : "You share your birthday with..."}
                </h2>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[...coinc.births].sort((a, b) => Number(b.year) - Number(a.year)).map((e) => (
                    <li key={e.id} data-testid={`twin-${e.id}`} className="flex items-center gap-3 bg-white border border-[#e5e7eb] rounded-xl px-3 py-2">
                      <img src="/images/categories/birth.png" alt="" className="w-9 h-9 rounded-lg bg-paper shrink-0" />
                      <span className="text-[14px] text-ink font-semibold truncate">{(e.event_title || "").replace(/^Birth of /i, "").replace(/ born$/i, "")}</span>
                      <span className="ml-auto text-[13px] font-bold text-gold">{e.year}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-baseline justify-between mb-1">
              <h2 data-testid="events-heading" className="text-2xl font-bold text-ink">{isToday ? `Today in History – ${dateLabel}` : `${dateLabel} in History`}</h2>
              <span data-testid="events-count" className="text-xs text-ink-muted">{countText}</span>
            </div>
            <p data-testid="events-subnote" className="mb-4 text-[12px] text-ink-muted">
              Births, deaths, battles, chart-toppers and events — in date order. Tap <strong className="text-ink font-semibold">See all</strong> on any line for the full archive.
            </p>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-10 rounded-xl bg-slate-100 animate-pulse" />)}
              </div>
            ) : (
              <>
                <TodayInHistoryList data={{ events, sports: data?.sports, births: coinc?.births }} hl={hl} ddmm={data?.date_dd_mm || ddmm} year={appliedYear} dateLabel={dateLabel} />
                <AdSlot {...AD_IN1} className="my-4" />
              </>
            )}
            <OnThisDayReel ddmm={data?.date_dd_mm || ddmm} />
            <HallOfFameReel ddmm={data?.date_dd_mm || ddmm} />
          </section>

          <section id="horoscope" className="scroll-mt-24 lg:sticky lg:top-24">
            <HoroscopePanel horoscope={horoscope} activeSign={activeSign} setActiveSign={setActiveSign} />
          </section>
        </div>

        <MoviesSection ddmm={data?.date_dd_mm || ddmm} dateLabel={isToday ? "today" : dateLabel} limit={3} />

        <section id="date-picker" className="mt-10">{datePicker}</section>

        <section id="coincidences" className="mt-10">
          {!loading && <Coincidences coinc={coinc} hl={hl} dateLabel={isToday ? "today" : dateLabel} />}
        </section>

        <ObservanceSection ddmm={data?.date_dd_mm || ddmm} />
        <NumberOnesSection ddmm={data?.date_dd_mm || ddmm} />
        <SportsSection events={data?.sports || []} dateLabel={isToday ? "today" : dateLabel} />
        <BattlesSection ddmm={data?.date_dd_mm || ddmm} dateLabel={isToday ? "today" : dateLabel} />
      </div>

      <WhyStrip />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <AdSlot id="adsense-footer" slot="FOOTER" size="leaderboard" />
      </div>
      <MobileBirthdayBar />
      <Footer />
    </div>
  );
}
